import React, { useState, useEffect, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Clock, X, CheckCircle, Loader, Crown } from 'lucide-react';
import GlobalLoader from './GlobalLoader';
import Swal from 'sweetalert2';
import { checkEmailAvailability, checkPhoneAvailability } from '../services/FirebaseService';

const QuickBooking = () => {
   // Estados
  const [selectedSpecialty, setSelectedSpecialty] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showRegistration, setShowRegistration] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [availabilityData, setAvailabilityData] = useState({});
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [campaignSpecialties, setCampaignSpecialties] = useState([]);
  const [leadData, setLeadData] = useState({
    leadId: null,
    email: '',
    password: ''
  });
  const [userData, setUserData] = useState({
    email: '',
    password: '',
    nombre: '',
    apellido: '',
    telefono: '',
    pronoun: '1',
    birthDate: '',
    birthSex: 'female'
  });
  // Leer parámetros UTM de la URL
  const [utmParams, setUtmParams] = useState({ // check
    campaign: 'DefaultCampaign',
    medium: 'Website',
    source: 'Website'
  });

  const API_URL = 'https://odoo.core.daisies.mx/marketing-manager/import-data';

// Leer parámetros UTM al cargar el componente
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const utmCampaign = urlParams.get('utm_campaign') || 'DefaultCampaign';
    const utmMedium = urlParams.get('utm_medium') || 'Website';
    const utmSource = urlParams.get('utm_source') || 'Website';

    setUtmParams({
      campaign: utmCampaign,
      medium: utmMedium,
      source: utmSource
    });

    // Obtener especialidades de la campaña
    fetchCampaignSpecialties(utmCampaign, utmMedium, utmSource);
  }, []);

  // Función para obtener especialidades de la campaña
  const fetchCampaignSpecialties = async (campaign, medium, source) => {
    
    try {
      // Paso 1: Buscar el ID de la campaña por su nombre
      const searchResponse = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          params: {
            res_model: "utm.campaign",
            res_method: "search_read",
            res_id: 1,
            res_params: {
              campaign_name: campaign
            }
          }
        })
      });

      const searchData = await searchResponse.json();
      
      let campaignId = null;
      
      // Si encontramos la campaña por nombre
      if (searchData.result?.success && searchData.result?.data?.campaign_id) {
        campaignId = searchData.result.data.campaign_id;
      } else {
        // Fallback: usar un ID por defecto para DefaultCampaign
        if (campaign === 'DefaultCampaign') {
          // ID de DefaultCampaign en Odoo
          campaignId = 36; // ID real de DefaultCampaign en Odoo
        }
      }

      if (!campaignId) {
        throw new Error(`No se pudo encontrar la campaña: ${campaign}`);
      }

      // Paso 2: Obtener los datos de la campaña usando su ID
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          params: {
            res_model: "utm.campaign",
            res_method: "get_campaign_record",
            res_id: campaignId,
            res_params: {
              utm_campaign: campaign,
              utm_source: source,
              utm_medium: medium
            }
          }
        })
      });

      const data = await response.json();

      if (data.result?.success && data.result?.data?.specialties) {
        const campaignSpecialtiesData = data.result.data.specialties;
        setCampaignSpecialties(campaignSpecialtiesData);
      } else {
        // Si no hay especialidades específicas, usar array vacío para mostrar todas
        setCampaignSpecialties([]);
      }
    } catch (error) {
      // En caso de error, usar array vacío para mostrar todas las especialidades
      setCampaignSpecialties([]);
    }
  };

  // Hardcoded fallback config (used when campaign API fails or returns no specialties)
  const fallbackSpecialtyConfig = {
    'ginecologia': { id: 15, duration: 1, extraDuration: null, name: 'Ginecología', icon: '🌸' },
    'psicologia': { id: 16, duration: 109, extraDuration: 109, name: 'Psicología', icon: '💭' },
    'salud-general': { id: 18, duration: 28, extraDuration: null, name: 'Salud General', icon: '🩺' },
    'skincare': { id: 18, duration: 28, extraDuration: null, name: 'Skincare', icon: '✨' },
    'entrenamiento': { id: 28, duration: 129, extraDuration: null, name: 'Entrenamiento', icon: '💪' },
    'nutricion': { id: 17, duration: 30, extraDuration: null, name: 'Nutrición', icon: '🥗' }
  };

  // Build dynamic config from campaign API response
  const specialtyConfig = useMemo(() => {
    if (campaignSpecialties.length === 0) return fallbackSpecialtyConfig;
    
    const config = {};
    campaignSpecialties.forEach(cs => {
      // Normalize specialty name to key: "Psicología" -> "psicologia"
      const key = cs.specialty.name
        .toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, '-');
      
      // cs.duration is the campaign's DEFAULT (could be extra!)
      // cs.specialty.durations = standard durations for this specialty
      // cs.specialty.extra_durations = extra durations
      const standardDurations = cs.specialty.durations || [];
      const extraDurations = cs.specialty.extra_durations || [];
      
      // Included = first standard non-extra duration
      const includedDur = standardDurations.find(d => !d.is_extra_duration) || standardDurations[0];
      // Extra = first from extra_durations array
      const extraDur = extraDurations[0] || null;
      
      config[key] = {
        id: cs.specialty.id,
        duration: includedDur?.id || fallbackSpecialtyConfig[key]?.duration || 109,
        extraDuration: extraDur?.id || null,
        allDurations: standardDurations,
        allExtraDurations: extraDurations,
        name: cs.specialty.name,
        icon: fallbackSpecialtyConfig[key]?.icon || '💫',
      };
    });
    return Object.keys(config).length > 0 ? config : fallbackSpecialtyConfig;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignSpecialties]);

  // Filtrar especialidades según la campaña
  const getFilteredSpecialties = () => {
    
    if (campaignSpecialties.length === 0) {
      // Si no hay especialidades de campaña (array vacío), mostrar todas
      return Object.entries(specialtyConfig);
    }

    // Filtrar solo las especialidades que están en la campaña
    const filtered = Object.entries(specialtyConfig).filter(([key, spec]) => {
      const isInCampaign = campaignSpecialties.some(campaignSpec => 
        campaignSpec.specialty.id === spec.id
      );
      return isInCampaign;
    });

    return filtered;
  };

  const filteredSpecialties = getFilteredSpecialties();
  const config = selectedSpecialty ? specialtyConfig[selectedSpecialty] : null;
  const specialtyId = config?.id;
  // Use the slot's duration_id if a slot is selected, otherwise fall back to config
  const durationId = selectedSlot?.duration_id || config?.duration;

  /* ------------------ VALIDACIONES CON FIREBASE ------------------ */
  const validateEmailWithFirebase = async (email) => {
    if (!email) return { isValid: false, message: 'El correo electrónico es requerido' };
    
    // Validación básica de formato
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { isValid: false, message: 'El formato del correo electrónico no es válido' };
    }
    
    // Validación de longitud
    if (email.length > 100) {
      return { isValid: false, message: 'El correo debe tener máximo 100 caracteres' };
    }
    
    try {
      const isAvailable = await checkEmailAvailability(email);
      
      if (!isAvailable) {
        return { 
          isValid: false, 
          message: 'Este correo electrónico ya está registrado. Por favor usa otro.' 
        };
      }
      
      return { isValid: true, message: '' };
    } catch (error) {
      return { 
        isValid: false, 
        message: 'Error al verificar el correo. Por favor intenta de nuevo.' 
      };
    }
  };

  const validatePhoneWithFirebase = async (phone) => {
    if (!phone) return { isValid: false, message: 'El teléfono es requerido' };
    
    // Remover espacios, guiones, paréntesis, etc.
    const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');
    
    // Validar que sean solo números
    if (!/^\d+$/.test(cleanPhone)) {
      return { isValid: false, message: 'El teléfono debe contener solo números' };
    }
    
    // Validar longitud exacta de 10 dígitos
    if (cleanPhone.length !== 10) {
      return { isValid: false, message: 'El teléfono debe tener exactamente 10 dígitos' };
    }
    
    try {
      const isAvailable = await checkPhoneAvailability(cleanPhone);
      
      if (!isAvailable) {
        return { 
          isValid: false, 
          message: 'Este número de teléfono ya está registrado. Por favor usa otro.' 
        };
      }
      
      return { isValid: true, message: '' };
    } catch (error) {
      return { 
        isValid: false, 
        message: 'Error al verificar el teléfono. Por favor intenta de nuevo.' 
      };
    }
  };

  const validatePassword = (password) => {
    if (!password) return { isValid: false, message: 'La contraseña es requerida' };
    
    const minLength = 8;
    const hasLetter = /[A-Za-z]/.test(password);
    const hasNumber = /\d/.test(password);
    
    if (password.length < minLength) {
      return { isValid: false, message: `La contraseña debe tener al menos ${minLength} caracteres` };
    }
    
    if (!hasLetter) {
      return { isValid: false, message: 'La contraseña debe contener al menos 1 letra' };
    }
    
    if (!hasNumber) {
      return { isValid: false, message: 'La contraseña debe contener al menos 1 número' };
    }
    
    return { isValid: true, message: '' };
  };

  const validateBirthDate = (birthDate) => {
    if (!birthDate) return { isValid: false, message: 'La fecha de nacimiento es requerida' };
    
    const birth = new Date(birthDate);
    const today = new Date();
    
    if (birth >= today) {
      return { isValid: false, message: 'La fecha de nacimiento debe ser menor a la fecha actual' };
    }
    
    const age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    const hasBirthdayPassed = monthDiff > 0 || (monthDiff === 0 && today.getDate() >= birth.getDate());
    const actualAge = hasBirthdayPassed ? age : age - 1;
    
    if (actualAge < 18) {
      return { isValid: false, message: 'Debes ser mayor de 18 años para agendar una cita' };
    }
    
    return { isValid: true, message: '' };
  };

  // Función de validación principal actualizada (async)
  const validateForm = async () => {
    // Validar campos básicos primero
    if (!userData.nombre.trim()) {
      Swal.fire({
        icon: 'error',
        title: 'Nombre requerido',
        text: 'Por favor ingresa tu nombre',
        confirmButtonColor: '#786ED9'
      });
      return false;
    }

    if (!userData.apellido.trim()) {
      Swal.fire({
        icon: 'error',
        title: 'Apellido requerido',
        text: 'Por favor ingresa tu apellido',
        confirmButtonColor: '#786ED9'
      });
      return false;
    }

    // Validar email con Firebase
    const emailValidation = await validateEmailWithFirebase(userData.email);
    if (!emailValidation.isValid) {
      Swal.fire({
        icon: 'error',
        title: 'Error en el correo electrónico',
        text: emailValidation.message,
        confirmButtonColor: '#786ED9'
      });
      return false;
    }

    // Validar teléfono con Firebase
    const phoneValidation = await validatePhoneWithFirebase(userData.telefono);
    if (!phoneValidation.isValid) {
      Swal.fire({
        icon: 'error',
        title: 'Error en el teléfono',
        text: phoneValidation.message,
        confirmButtonColor: '#786ED9'
      });
      return false;
    }

    // Validar contraseña
    const passwordValidation = validatePassword(userData.password);
    if (!passwordValidation.isValid) {
      Swal.fire({
        icon: 'error',
        title: 'Error en la contraseña',
        html: passwordValidation.message,
        confirmButtonColor: '#786ED9'
      });
      return false;
    }

    // Validar fecha de nacimiento
    const birthDateValidation = validateBirthDate(userData.birthDate);
    if (!birthDateValidation.isValid) {
      Swal.fire({
        icon: 'error',
        title: 'Error en la fecha de nacimiento',
        text: birthDateValidation.message,
        confirmButtonColor: '#786ED9'
      });
      return false;
    }

    // Validar términos y condiciones
    if (!acceptedTerms) {
      Swal.fire({
        icon: 'error',
        title: 'Términos y condiciones',
        text: 'Debes aceptar los términos y condiciones para continuar',
        confirmButtonColor: '#786ED9'
      });
      return false;
    }

    return true;
  };

  /* ------------------ FUNCIÓN PARA REDIRECCIÓN ------------------ */
  const handleCompleteMedicalRecord = () => {
    if (!leadData.leadId || !leadData.email || !leadData.password) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se encontraron los datos necesarios para completar el expediente médico',
        confirmButtonColor: '#786ED9'
      });
      return;
    }

    // Construir URL con parámetros
    const params = new URLSearchParams({
      leadId: leadData.leadId.toString(),
      email: leadData.email,
      password: leadData.password
    });

    const redirectUrl = `https://app.daisies.mx/authPage?${params.toString()}`;
    
    
    // Redireccionar
    window.location.href = redirectUrl;
  };

  /* ------------------ LOG + FETCH (DUAL CALLS: included + extra) ------------------ */
  const fetchAvailabilityByDuration = async (specId, durId, formattedDate, tz) => {
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        params: {
          res_model: 'dais.consultation',
          res_method: 'get_availble_consultation_blocks_for_externals',
          res_id: 1,
          access_token: process.env.REACT_APP_ACCESS_TOKEN,
          account_id: 1,
          res_params: {
            specialty_id: specId,
            duration_id: durId,
            date: formattedDate,
            tz,
            utm_campaign: utmParams.campaign,
            utm_medium: utmParams.medium,
            utm_source: utmParams.source
          }
        }
      })
    });
    return response.json();
  };

  const fetchAvailability = async (date, overrideSpecialtyKey = null) => {
    const specKey = overrideSpecialtyKey || selectedSpecialty;
    const specConfig = specialtyConfig[specKey];
    if (!specKey || !specConfig) {
      return;
    }

    const usedSpecialtyId = specConfig.id;
    const formattedDate = date.toISOString().split('T')[0];
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Mexico_City';

    // Build list of ALL unique durations to fetch
    const durationFetchList = [];
    const seenIds = new Set();

    (specConfig.allDurations || []).forEach(d => {
      if (d?.id && !seenIds.has(d.id)) {
        seenIds.add(d.id);
        durationFetchList.push({ id: d.id, isExtra: d.is_extra_duration || false });
      }
    });

    (specConfig.allExtraDurations || []).forEach(d => {
      if (d?.id && !seenIds.has(d.id)) {
        seenIds.add(d.id);
        durationFetchList.push({ id: d.id, isExtra: true });
      }
    });

    // Fallback: if no duration arrays, use legacy included + extra
    if (durationFetchList.length === 0) {
      const includedDurationId = specConfig.duration;
      if (includedDurationId) {
        seenIds.add(includedDurationId);
        durationFetchList.push({ id: includedDurationId, isExtra: false });
      }
      const extraDurationId = specConfig.extraDuration;
      if (extraDurationId && !seenIds.has(extraDurationId)) {
        durationFetchList.push({ id: extraDurationId, isExtra: true });
      }
    }

    console.log('[QuickBooking.fetchAvailability] Fetching for durations:', durationFetchList);

    setCalendarLoading(true);
    try {
      // Fetch ALL durations in parallel
      const allResults = await Promise.all(
        durationFetchList.map(d =>
          fetchAvailabilityByDuration(usedSpecialtyId, d.id, formattedDate, tz)
            .then(data => ({ data, durObj: d }))
            .catch(err => {
              console.warn(`[QuickBooking] Error for duration ${d.id}:`, err);
              return null;
            })
        )
      );

      const organized = {};

      const processSlots = (data, isExtra, durationId) => {
        if (data?.result?.success && data?.result?.data) {
          data.result.data.forEach(dayData => {
            if (!organized[dayData.date]) {
              organized[dayData.date] = [];
            }
            dayData.schedule_available.forEach(slot => {
              const exists = organized[dayData.date].some(s => s.id === slot.id && s.init === slot.init);
              if (!exists) {
                organized[dayData.date].push({
                  ...slot,
                  duration_id: durationId,
                  isExtra: isExtra,
                });
              }
            });
          });
        }
      };

      allResults.forEach(result => {
        if (result) {
          processSlots(result.data, result.durObj.isExtra, result.durObj.id);
        }
      });

      // Sort each date's slots by time
      Object.keys(organized).forEach(d => {
        organized[d].sort((a, b) => {
          const timeA = a.init.split(':').map(Number);
          const timeB = b.init.split(':').map(Number);
          return (timeA[0] * 60 + timeA[1]) - (timeB[0] * 60 + timeB[1]);
        });
      });

      setAvailabilityData(organized);
    } catch (error) {
      alert('Error al obtener disponibilidad. Por favor intenta de nuevo.');
    } finally {
      setCalendarLoading(false);
    }
  };

  // useEffect: se dispara cuando cambia selectedSpecialty (cuando eliges otra especialidad)
  useEffect(() => {
    if (selectedSpecialty) {
      // fetch para la fecha de hoy
      const startDate = new Date();
      fetchAvailability(startDate);
    } else {
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSpecialty]);

  /* ------------------ HANDLERS ------------------ */
  const handleSpecialtySelect = (specialtyKey) => {

    // limpiar estado relacionado
    setSelectedDate(null);
    setSelectedTime(null);
    setSelectedSlot(null);
    setAvailabilityData({});

    if (specialtyKey === selectedSpecialty) {
      // Si el usuario clickea la misma especialidad, forzamos un re-fetch
      fetchAvailability(new Date(), specialtyKey);
    } else {
      // Cambia el estado: useEffect detectará y hará el fetch
      setSelectedSpecialty(specialtyKey);
    }
  };

  const daysOfWeek = ['DOM.', 'LUN.', 'MAR.', 'MIÉ.', 'JUE.', 'VIE.', 'SÁB.'];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    return { daysInMonth, startingDayOfWeek };
  };

  const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentMonth);

  const previousMonth = () => {
    const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    setCurrentMonth(newMonth);
    setSelectedDate(null);
  };

  const nextMonth = () => {
    const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    setCurrentMonth(newMonth);
    setSelectedDate(null);
  };

  const handleDateClick = (day) => {
    const clickedDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const formattedDate = clickedDate.toISOString().split('T')[0];
    const hasSlots = availabilityData[formattedDate] && availabilityData[formattedDate].length > 0;

    if (!selectedSpecialty) {
      return;
    }
    if (!hasSlots) {
      return;
    }

    setSelectedDate(clickedDate);
    setSelectedTime(null);
    setSelectedSlot(null);
    setShowRegistration(false);
    setShowConfirmation(false);

    // Si no tenemos los slots para esa fecha (no en availabilityData) — fetch (aunque normalmente ya vienen)
    if (!availabilityData[formattedDate]) {
      fetchAvailability(clickedDate);
    } else {
    }
  };

  const handleTimeClick = (slot) => {
    setSelectedSlot(slot);
    setSelectedTime(slot.init);
    setShowRegistration(true);
  };

  // Enviar reserva a la API - ACTUALIZADO
  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!selectedSlot || !selectedSpecialty) {
      alert('Selecciona una especialidad y un horario antes de reservar.');
      return;
    }

    setLoading(true);

    try {
      // Validar formulario con Firebase (async)
      const isValid = await validateForm();
      if (!isValid) {
        setLoading(false);
        return;
      }

      const payload = {
        params: {
          res_model: "crm.lead",
          res_method: "create_lead_from_campaign",
          res_id: 1,
          res_params: {
            name: userData.nombre,
            lastname: userData.apellido,
            email: userData.email,
            password: userData.password,
            phone: userData.telefono,
            pronoun_id: userData.pronoun ? parseInt(userData.pronoun, 10) : 1,
            birth_date: userData.birthDate,
            birth_sex: userData.birthSex,
            tz: Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Mexico_City",
            utm_campaign: utmParams.campaign,
            utm_medium: utmParams.medium,
            utm_source: utmParams.source,
            schedule_id: selectedSlot.id,
            professional_ids: selectedSlot.professional_ids.map(p => p.id),
            duration_id: durationId,
            specialty_id: specialtyId
          }
        }
      };

      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.result?.success) {
        
        // Guardar datos del lead para la redirección
        setLeadData({
          leadId: data.result.lead_id,
          email: userData.email,
          password: userData.password
        });

        setShowRegistration(false);
        setShowConfirmation(true);
      } else {
        alert("Hubo un problema al reservar la cita. Intenta de nuevo.");
      }
    } catch (error) {
      alert("Error al reservar la cita. Intenta más tarde.");
    } finally {
      setLoading(false);
    }
  };

  /* ------------------ RENDER CALENDAR ------------------ */
  const monthName = currentMonth.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });

  const renderCalendar = () => {
    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="aspect-square" />);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      const dayDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      const formattedDate = dayDate.toISOString().split('T')[0];
      const hasSlots = selectedSpecialty && availabilityData[formattedDate] && availabilityData[formattedDate].length > 0;
      const isSelected = selectedDate && selectedDate.getDate() === day;

      days.push(
        <button
          key={day}
          onClick={() => hasSlots && handleDateClick(day)}
          disabled={!hasSlots || calendarLoading}
          className={`
            aspect-square rounded-lg flex items-center justify-center text-sm font-medium transition-all
            ${hasSlots ? 'hover:bg-daisies hover:text-white cursor-pointer bg-daisies-50 text-daisies font-bold' : 'text-gray-300 cursor-not-allowed'}
            ${isSelected ? 'bg-daisies text-white scale-110 shadow-soft' : ''}
          `}
        >
          {day}
        </button>
      );
    }
    return days;
  };

  const getAvailableTimesForSelectedDate = () => {
    if (!selectedDate) return [];
    const formattedDate = selectedDate.toISOString().split('T')[0];
    return availabilityData[formattedDate] || [];
  };

  /* ------------------ JSX ------------------ */
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white pt-8 flex items-center justify-center">
      <div className="w-full max-w-6xl bg-white rounded-3xl shadow-2xl overflow-hidden">
        <div className="grid md:grid-cols-2">
          {/* Left */}
          <div className="p-12 bg-gradient-to-br from-daisies to-[#9F94E8] text-white flex flex-col justify-center">
            <div className="mb-8">
              <div className="text-6xl mb-6">👩‍⚕️</div>
              <h2 className="text-4xl font-bold mb-4">Agenda una sesión ahora.</h2>
              <p className="text-xl opacity-90">Te ayudamos con eso que te impide avanzar. ¡Tú primera sesión, va por nuestra cuenta!</p>
            </div>
          </div>

          {/* Right */}
          <div className="p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Agenda una llamada</h3>

            {/* Grid de especialidades - FILTRADO POR CAMPAÑA */}
            <div className="grid grid-cols-3 gap-4 mb-8">
              {filteredSpecialties.map(([key, spec]) => (
                <button
                  key={key}
                  onClick={() => handleSpecialtySelect(key)}
                  className={`
                    flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all
                    ${selectedSpecialty === key
                      ? 'border-daisies bg-daisies-50 text-daisies font-bold scale-105 shadow-soft'
                      : 'border-black/10 hover:border-daisies hover:bg-daisies-50'}
                  `}
                >
                  <div className="text-3xl mb-2">{spec.icon}</div>
                  <div className="text-sm text-center">{spec.name}</div>
                </button>
              ))}
            </div>

            {/* Calendario siempre visible */}
            <div className="flex items-center justify-between mb-6">
              <button onClick={previousMonth} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <ChevronLeft className="text-gray-600" />
              </button>
              <span className="text-lg font-semibold text-gray-900 capitalize">{monthName}</span>
              <button onClick={nextMonth} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <ChevronRight className="text-gray-600" />
              </button>
            </div>

            {/* Loader para calendario */}
            {calendarLoading && (
              <GlobalLoader title="Cargando calendario" subtitle="Buscando disponibilidad..." overlay={false} />
            )}

            {/* Days header */}
            <div className="grid grid-cols-7 gap-2 mb-3">
              {daysOfWeek.map((day) => (
                <div key={day} className="text-center text-xs font-semibold text-gray-500 uppercase">{day}</div>
              ))}
            </div>

            {/* Calendar */}
            <div className="grid grid-cols-7 gap-2 mb-6">{renderCalendar()}</div>

            {/* Horarios */}
            {selectedDate && (
              <div className="mt-6 animate-fadeIn">
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="text-daisies" size={20} />
                  <h4 className="font-semibold text-gray-900">
                    Horarios disponibles - {selectedDate.getDate()} de {selectedDate.toLocaleDateString('es-MX', { month: 'long' })}
                  </h4>
                </div>
                {loading ? (
                  <GlobalLoader title="Cargando horarios" subtitle="Buscando disponibilidad..." overlay={false} />
                ) : getAvailableTimesForSelectedDate().length > 0 ? (
                  <div className="grid grid-cols-2 gap-3 max-h-64 overflow-y-auto">
                    {getAvailableTimesForSelectedDate().map((slot) => {
                      const isIncludedSlot = slot.isExtra === false;
                      return (
                        <button
                          key={`${slot.id}-${slot.init}-${slot.isExtra ? 'extra' : 'included'}`}
                          onClick={() => {
                            if (isIncludedSlot) {
                              Swal.fire({
                                icon: 'info',
                                title: 'Espacio con membresía',
                                text: 'Este espacio está disponible solo con membresía, ¿deseas continuar?',
                                showCancelButton: true,
                                confirmButtonText: 'Continuar',
                                cancelButtonText: 'Cancelar',
                                confirmButtonColor: '#786ED9',
                                cancelButtonColor: '#9CA3AF'
                              }).then((result) => {
                                if (result.isConfirmed) handleTimeClick(slot);
                              });
                            } else {
                              handleTimeClick(slot);
                            }
                          }}
                          className={`px-4 py-3 border-2 rounded-xl font-semibold transition-all text-center text-sm ${
                            isIncludedSlot
                              ? 'border-daisies/30 text-daisies bg-daisies-50 hover:bg-daisies hover:text-white'
                              : 'border-daisies text-daisies hover:bg-daisies hover:text-white'
                          }`}
                        >
                          <div className="flex items-center justify-center gap-1">
                            {slot.init} - {slot.end}
                            {isIncludedSlot && <Crown size={14} className="text-daisies" />}
                          </div>
                          <div className="text-xs opacity-75 mt-1">
                            {slot.professional_ids?.length || 0} doctora{(slot.professional_ids?.length || 0) > 1 ? 's' : ''} disponible{(slot.professional_ids?.length || 0) > 1 ? 's' : ''}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">No hay horarios disponibles para esta fecha</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal registro */}
      {showRegistration && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fadeIn">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl animate-slideUp">
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Completa tu Pre-Registro</h3>
              <button onClick={() => { setShowRegistration(false); }} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleFormSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <input 
                  type="text" 
                  required 
                  placeholder="Nombre" 
                  value={userData.nombre} 
                  onChange={(e) => setUserData({ ...userData, nombre: e.target.value })} 
                  className="px-4 py-3 border-2 rounded-xl" 
                />
                <input 
                  type="text" 
                  required 
                  placeholder="Apellido" 
                  value={userData.apellido} 
                  onChange={(e) => setUserData({ ...userData, apellido: e.target.value })} 
                  className="px-4 py-3 border-2 rounded-xl" 
                />
              </div>
              <input 
                type="email" 
                required 
                placeholder="Correo electrónico" 
                value={userData.email} 
                onChange={(e) => setUserData({ ...userData, email: e.target.value })} 
                className="w-full px-4 py-3 border-2 rounded-xl" 
                maxLength={100}
              />
              <input 
                type="password" 
                required 
                placeholder="Contraseña (mín. 8 caracteres, 1 letra y 1 número)" 
                value={userData.password} 
                onChange={(e) => setUserData({ ...userData, password: e.target.value })} 
                className="w-full px-4 py-3 border-2 rounded-xl" 
                minLength={8}
              />
              <input 
                type="tel" 
                required 
                placeholder="Teléfono (10 dígitos)" 
                value={userData.telefono} 
                onChange={(e) => setUserData({ ...userData, telefono: e.target.value })} 
                className="w-full px-4 py-3 border-2 rounded-xl" 
                maxLength={10}
                pattern="[0-9]{10}"
              />
              <input 
                type="date" 
                required 
                value={userData.birthDate} 
                onChange={(e) => setUserData({ ...userData, birthDate: e.target.value })} 
                className="w-full px-4 py-3 border-2 rounded-xl" 
                max={new Date().toISOString().split('T')[0]}
              />
              <div className="grid grid-cols-2 gap-4">
                <select required value={userData.birthSex} onChange={(e) => setUserData({ ...userData, birthSex: e.target.value })} className="px-4 py-3 border-2 rounded-xl">
                  <option value="female">Femenino</option>
                  <option value="male">Masculino</option>
                  <option value="intersex">Intersexual</option>
                </select>
                <select required value={userData.pronoun} onChange={(e) => setUserData({ ...userData, pronoun: e.target.value })} className="px-4 py-3 border-2 rounded-xl">
                  <option value="1">Ella</option>
                  <option value="2">Él</option>
                  <option value="3">Elle</option>
                </select>
              </div>

              {/* Términos y condiciones */}
              <div className="flex items-start space-x-3 pt-2">
                <input
                  type="checkbox"
                  id="terms"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-1 rounded border-gray-300 text-daisies focus:ring-daisies"
                />
                <label htmlFor="terms" className="text-sm text-gray-600">
                  Acepto los{' '}
                  <a 
                    href="https://daisies.mx/terminos-y-condiciones" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-daisies hover:text-daisies-hover underline font-semibold"
                  >
                    términos y condiciones
                  </a>
                </label>
              </div>

              <button 
                type="submit" 
                disabled={loading}
                className="w-full px-6 py-3 bg-daisies text-white rounded-full font-semibold hover:bg-daisies-hover transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Procesando...' : 'Reservar cita'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Modal confirmación */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fadeIn">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-2xl animate-slideUp">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-900 mb-2">¡Cita reservada exitosamente!</h3>
            
            <div className="text-left bg-purple-50 rounded-xl p-4 mb-6">
              <p className="text-gray-700 mb-3">
                <span className="font-semibold">Hola {userData.nombre},</span> tu cita ha sido registrada con éxito.
              </p>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Especialidad:</span>
                  <span className="font-semibold">{specialtyConfig[selectedSpecialty]?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Fecha:</span>
                  <span className="font-semibold">
                    {selectedDate?.getDate()} de {selectedDate?.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Horario:</span>
                  <span className="font-semibold">{selectedTime}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Correo:</span>
                  <span className="font-semibold">{userData.email}</span>
                </div>
              </div>
            </div>

            <p className="text-gray-600 mb-2 text-sm">
              Te hemos enviado un correo de confirmación con todos los detalles.
            </p>
            <p className="text-gray-600 mb-6 text-sm">
              Para completar tu registro y acceder a tu historial médico, continúa en nuestra app.
            </p>

            <div className="flex gap-3">
              <button 
                onClick={() => setShowConfirmation(false)} 
                className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-full font-semibold hover:bg-gray-50 transition-all"
              >
                Cerrar
              </button>
              <button 
                onClick={handleCompleteMedicalRecord}
                className="flex-1 px-4 py-3 bg-daisies text-white rounded-full font-semibold hover:bg-daisies-hover transition-all text-center"
              >
                Completar Expediente Médico
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px);} to { opacity: 1; transform: translateY(0);} }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
        .animate-slideUp { animation: slideUp 0.3s ease-out; }
      `}</style>
    </div>
  );
};

export default QuickBooking;